import React, { useState, useEffect, useCallback } from "react";
import { ClipboardList, Building2, UserPlus, Baby, CheckCircle2, XCircle, RefreshCw, Mail, Eye, HeartHandshake, X } from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccBtnPink, ccBtnGhost, ccInput, ccLabel } from "./care/ui";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLE = {
  pending: "bg-[#FFF2D1] border-[#F5E3B3] text-[#A08244]",
  approved: "bg-[#E6F2FA] border-[#B3E0F2] text-[#3D7E9E]",
  activated: "bg-[#E2F4E1] border-[#C4E5C2] text-[#2F6031]",
  declined: "bg-rose-50 border-rose-200 text-rose-500",
  suspended: "bg-rose-50 border-rose-200 text-rose-500",
};

const TABS = [
  { key: "applications", label: "Applications", icon: ClipboardList },
  { key: "facility", label: "Facility Setup", icon: Building2 },
  { key: "enrollment", label: "Enrollment", icon: UserPlus },
];

const CAREGIVER_TYPES = [
  "Babysitter",
  "Nanny",
  "Respite Care",
  "Special Needs Support",
  "After-School Care",
  "Newborn & Infant Care",
  "Tutor / Learning Support",
  "Date-Night Sitter",
  "Other",
];

// Manual caregiver enrollment — creates the account and emails a claim link so they set up their own password
const CaregiverEnrollModal = ({ onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", caregiver_type: "Babysitter", headline: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/workspace/invite-caregiver`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Enrollment failed");
      if (body.claim_link) navigator.clipboard?.writeText(body.claim_link).catch(() => {});
      toast.success(
        body.email_sent === false
          ? `Caregiver account created for ${form.email}. Email is rate-limited right now — the claim link is copied; share it manually.`
          : `Caregiver account created — ${form.email} will get an email to set up their account. Claim link copied for manual sharing.`
      );
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#374151]/40 backdrop-blur-sm" onClick={onClose} data-testid="caregiver-enroll-modal">
      <form
        onSubmit={submit}
        className="bg-white rounded-3xl border border-[#FCE6E9] shadow-2xl max-w-md w-full p-6 space-y-4 dark:bg-[#273244] dark:border-[#374457]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100 flex items-center gap-2">
            <HeartHandshake className="w-4 h-4 text-[#E5A0BC]" /> Enroll Caregiver
          </h3>
          <button type="button" onClick={onClose} data-testid="caregiver-enroll-close" className="w-8 h-8 rounded-full bg-[#FFF9F2] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151]">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div>
          <label className={ccLabel}>Full Name</label>
          <input required type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={ccInput} data-testid="cg-enroll-name" placeholder="Jordan Rivera" />
        </div>
        <div>
          <label className={ccLabel}>Email (gets the account setup link)</label>
          <input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={ccInput} data-testid="cg-enroll-email" placeholder="caregiver@example.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={ccLabel}>Phone</label>
            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={ccInput} data-testid="cg-enroll-phone" placeholder="(555) 123-4567" />
          </div>
          <div>
            <label className={ccLabel}>Caregiver Type</label>
            <select value={form.caregiver_type} onChange={(e) => set("caregiver_type", e.target.value)} className={ccInput} data-testid="cg-enroll-type">
              {CAREGIVER_TYPES.map((t) => (
                <option key={t}>{t}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className={ccLabel}>Headline (optional)</label>
          <input type="text" value={form.headline} onChange={(e) => set("headline", e.target.value)} className={ccInput} data-testid="cg-enroll-headline" placeholder="e.g. CPR-certified infant specialist, evenings" />
        </div>
        <p className="text-[11px] text-[#9CA3AF] font-medium">
          The caregiver receives an email with a secure claim link — they set their own password, then publish their Care Connections profile.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={ccBtnGhost} data-testid="cg-enroll-cancel">Cancel</button>
          <button type="submit" disabled={busy} className={`${ccBtnPink} disabled:opacity-50`} data-testid="cg-enroll-submit">
            {busy ? "Sending…" : "Create & Email Invite"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default function MasterOnboarding({ onOpenOwnerModal, onOpenEnrollModal, onReviewAccount }) {
  const [tab, setTab] = useState("applications");
  const [apps, setApps] = useState(null);
  const [busy, setBusy] = useState("");
  const [showCgModal, setShowCgModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/master/onboarding`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const body = await res.json();
      setApps(body.applications);
    } catch {
      toast.error("Could not load onboarding applications.");
    }
  }, []);

  useEffect(() => {
    load();
    const onChanged = () => load();
    window.addEventListener("nt:onboarding-changed", onChanged);
    return () => window.removeEventListener("nt:onboarding-changed", onChanged);
  }, [load]);

  const review = async (appId, action) => {
    setBusy(`${appId}:${action}`);
    try {
      const res = await fetch(`${API}/api/master/onboarding/${appId}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Action failed");
      if (body.emailError) toast.warning(`${action === "approve" ? "Approved" : "Declined"} — but the email failed to send (${body.emailError}). You can retry later.`);
      else toast.success(action === "approve" ? "Approved — activation email sent." : "Application declined — notification email sent.");
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy("");
    }
  };

  const pendingCount = (apps || []).filter((a) => a.status === "pending").length;

  return (
    <section className={`${ccCard} p-6`} data-testid="master-onboarding">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#FFF2D1] flex items-center justify-center">
            <ClipboardList className="w-4 h-4 text-[#D9A93E]" />
          </span>
          Onboarding
          {pendingCount > 0 && (
            <span className="px-2.5 py-0.5 bg-[#F8C8DC] text-[#374151] text-[10px] font-extrabold rounded-full" data-testid="onboarding-pending-count">
              {pendingCount} pending
            </span>
          )}
        </h3>
        <div className="flex gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-full p-1" data-testid="onboarding-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`onboarding-tab-${t.key}`}
              className={`text-xs font-bold flex items-center gap-1.5 px-4 py-2 rounded-full transition-all duration-200 ${
                tab === t.key ? "bg-[#F8C8DC] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === "applications" && (
        <div data-testid="onboarding-applications-panel">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] text-[#9CA3AF] font-medium">
              Daycare signups from the landing page arrive here. Approving sends a real activation email to the owner.
            </p>
            <button
              onClick={load}
              data-testid="onboarding-refresh-btn"
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-[11px] font-bold transition-all"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>
          {apps === null ? (
            <p className="text-xs text-[#9CA3AF] font-medium py-6 text-center">Loading applications...</p>
          ) : apps.length === 0 ? (
            <div className="p-8 border-2 border-dashed border-[#FCE6E9] rounded-2xl text-center" data-testid="onboarding-empty">
              <p className="text-xs text-[#9CA3AF] font-medium">No daycare applications yet. New signups will appear here for review.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {apps.map((a) => (
                <div key={a.id} className="p-4 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl" data-testid={`application-row-${a.id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center text-[#5CB3DC] text-sm font-black font-heading shrink-0">
                        {a.facilityName.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-extrabold text-[#374151]" data-testid={`application-name-${a.id}`}>{a.facilityName}</p>
                        <p className="text-[11px] text-[#9CA3AF]">
                          {a.ownerName} · <span className="font-semibold">{a.email}</span>
                          {a.phone && ` · ${a.phone}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => onReviewAccount?.(a.email)}
                        data-testid={`application-review-${a.id}`}
                        className="flex items-center gap-1 px-3 py-1.5 bg-white border border-[#B3E0F2] text-[#3D7E9E] rounded-full text-[10px] font-bold hover:bg-[#E6F2FA] transition-colors"
                      >
                        <Eye className="w-3 h-3" /> Full Review
                      </button>
                      <span
                        className={`px-2.5 py-1 border text-[10px] font-extrabold uppercase rounded-full ${STATUS_STYLE[a.status] || STATUS_STYLE.pending}`}
                        data-testid={`application-status-${a.id}`}
                      >
                        {a.status}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-[11px]">
                    <p className="text-[#6B7280]"><span className="font-bold text-[#9CA3AF] uppercase text-[9px] block">License #</span>{a.licenseNumber || "—"}</p>
                    <p className="text-[#6B7280]"><span className="font-bold text-[#9CA3AF] uppercase text-[9px] block">Jurisdiction</span>{a.jurisdiction || "—"}</p>
                    <p className="text-[#6B7280]"><span className="font-bold text-[#9CA3AF] uppercase text-[9px] block">Tax ID</span>{a.taxId || "—"}</p>
                    <p className="text-[#6B7280]"><span className="font-bold text-[#9CA3AF] uppercase text-[9px] block">Capacity</span>{a.maxCapacity || "—"}</p>
                  </div>
                  {a.status === "pending" && (
                    <div className="flex gap-2 mt-3.5">
                      <button
                        onClick={() => review(a.id, "approve")}
                        disabled={busy}
                        data-testid={`application-approve-${a.id}`}
                        className="flex items-center gap-1.5 px-4 py-2 bg-[#E2F4E1] hover:bg-[#C4E5C2] border border-[#C4E5C2] text-[#2F6031] rounded-full text-xs font-bold transition-all disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {busy === `${a.id}:approve` ? "Approving..." : "Approve & Email Activation"}
                      </button>
                      <button
                        onClick={() => review(a.id, "decline")}
                        disabled={busy}
                        data-testid={`application-decline-${a.id}`}
                        className="flex items-center gap-1.5 px-4 py-2 bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-500 rounded-full text-xs font-bold transition-all disabled:opacity-50"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                        {busy === `${a.id}:decline` ? "Declining..." : "Decline"}
                      </button>
                    </div>
                  )}
                  {a.status === "approved" && (
                    <p className="mt-3 text-[11px] font-bold text-[#3D7E9E] flex items-center gap-1.5" data-testid={`application-email-note-${a.id}`}>
                      <Mail className="w-3.5 h-3.5" /> Activation email sent — waiting for the owner to activate.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "facility" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="onboarding-facility-panel">
          <div className="p-5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl">
            <span className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center mb-3">
              <Building2 className="w-4 h-4 text-[#5CB3DC]" />
            </span>
            <h4 className="text-sm font-extrabold font-heading text-[#374151] mb-1">Facility Licensing & Setup</h4>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
              Register a facility's trade name, state license number, jurisdiction, tax ID, and max capacity. Saved details appear on the daycare dashboard banner.
            </p>
            <button onClick={onOpenOwnerModal} data-testid="onboarding-open-facility-btn" className={ccBtnPink}>
              Open Licensing Form
            </button>
          </div>
          <div className="p-5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl flex flex-col justify-center">
            <p className="text-[11px] text-[#6B7280] font-medium leading-relaxed">
              Daycare owners also complete this step themselves from their own dashboard via
              <span className="font-extrabold text-[#374151]"> Setup Facility</span> or the
              <span className="font-extrabold text-[#374151]"> Complete Onboarding</span> banner — nothing was removed from the facility side.
            </p>
          </div>
        </div>
      )}

      {tab === "enrollment" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="onboarding-enrollment-panel">
          <div className="p-5 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl">
            <span className="w-10 h-10 rounded-full bg-[#FCE6E9] flex items-center justify-center mb-3">
              <UserPlus className="w-4 h-4 text-[#E5A0BC]" />
            </span>
            <h4 className="text-sm font-extrabold font-heading text-[#374151] mb-1">Staff / Parent Enrollment</h4>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
              Create staff, teacher, or parent profiles with photo, contact info, and role assignment.
            </p>
            <button onClick={() => onOpenEnrollModal("staff")} data-testid="onboarding-enroll-staff-btn" className={ccBtnPink}>
              Enroll Staff / Parent
            </button>
          </div>
          <div className="p-5 bg-[#FFF9F2] border border-[#E6F2FA] rounded-2xl">
            <span className="w-10 h-10 rounded-full bg-[#E6F2FA] flex items-center justify-center mb-3">
              <Baby className="w-4 h-4 text-[#5CB3DC]" />
            </span>
            <h4 className="text-sm font-extrabold font-heading text-[#374151] mb-1">Child Enrollment</h4>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
              Register a child with DOB, classroom assignment, allergy tags, and medical notes.
            </p>
            <button
              onClick={() => onOpenEnrollModal("child")}
              data-testid="onboarding-enroll-child-btn"
              className="w-full sm:w-auto px-5 py-2.5 bg-[#B3E0F2] hover:bg-[#9ACCE0] text-[#374151] rounded-full text-xs font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm shadow-blue-200"
            >
              Enroll Child
            </button>
          </div>
          <div className="p-5 bg-[#FFF9F2] border border-[#E2F4E1] rounded-2xl" data-testid="onboarding-caregiver-card">
            <span className="w-10 h-10 rounded-full bg-[#E2F4E1] flex items-center justify-center mb-3">
              <HeartHandshake className="w-4 h-4 text-[#2F6031]" />
            </span>
            <h4 className="text-sm font-extrabold font-heading text-[#374151] mb-1">Caregiver Enrollment</h4>
            <p className="text-[11px] text-[#9CA3AF] font-medium mb-4">
              Manually add any caregiver type — babysitter, nanny, respite, special needs &amp; more. They get an email to set up their account.
            </p>
            <button
              onClick={() => setShowCgModal(true)}
              data-testid="onboarding-enroll-caregiver-btn"
              className="w-full sm:w-auto px-5 py-2.5 bg-[#C4E5C2] hover:bg-[#a9d9a6] text-[#2F6031] rounded-full text-xs font-bold transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
            >
              Enroll Caregiver
            </button>
          </div>
        </div>
      )}

      {showCgModal && <CaregiverEnrollModal onClose={() => setShowCgModal(false)} />}
    </section>
  );
}
